import React, { useMemo, useState } from "react";
import { PageShell } from "../components/Layout";
import api from "../lib/api";
import { ArrowRight, Stack, Sparkle, Crown, Eye, ShieldWarning, Star } from "@phosphor-icons/react";
import { toast } from "sonner";
import KundaliChart, { useChartStyle } from "../components/KundaliChart";
import ToolAstrologerCta from "../components/ToolAstrologerCta";
import usePrefillBirthDetails from "../hooks/usePrefillBirthDetails";
import PaywallGate from "../components/PaywallGate";

const DIGNITY_TINT = {
  exalted: { bg: "bg-emerald-100", text: "text-emerald-900", label: "EXALTED", icon: Crown },
  debilitated: { bg: "bg-rose-100", text: "text-rose-900", label: "DEBILITATED", icon: ShieldWarning },
  own_sign: { bg: "bg-amber-100", text: "text-amber-900", label: "OWN SIGN", icon: Star },
  mooltrikona: { bg: "bg-violet-100", text: "text-violet-900", label: "MOOLTRIKONA", icon: Sparkle },
  neutral: { bg: "bg-slate-100", text: "text-slate-800", label: "NEUTRAL", icon: Eye },
};

export default function DivisionalChartsPage() {
  const [form, setForm] = useState({ date: "", time: "", place: "", timezone: "Asia/Kolkata" });
  const [placeQuery, setPlaceQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [selectedDiv, setSelectedDiv] = useState("9");
  const chartStyle = useChartStyle();
  usePrefillBirthDetails(setForm, setPlaceQuery);

  const submit = async (e) => {
    e?.preventDefault();
    const place = form.place || placeQuery;
    if (!form.date || !form.time || !place) {
      toast.error("Please fill date, time and place");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/divisional/compute", { ...form, place });
      setData(r.data);
      setSelectedDiv("9");
      setTimeout(() => document.querySelector("[data-testid='divisional-result']")?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to compute divisional charts");
    } finally {
      setLoading(false);
    }
  };

  const chart = useMemo(() => data?.charts?.[selectedDiv], [data, selectedDiv]);

  const chartShape = useMemo(() => {
    if (!chart) return null;
    return {
      lagna: { rashi: chart.lagna.rashi },
      planets: Object.fromEntries(
        Object.entries(chart.planets).map(([name, p]) => [
          name, { rashi: p.rashi, retrograde: !!p.retrograde },
        ])
      ),
    };
  }, [chart]);

  return (
    <PageShell>
      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10 pt-10 pb-20">
        <header className="mb-10">
          <div className="label-eyebrow text-muted flex items-center gap-2">
            <Stack size={14} weight="duotone" /> Divisional Charts · D1 → D60 · Vargas
          </div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3 font-light leading-tight">
            All your <span className="text-gold">divisional charts</span>, in one calculator
          </h1>
          <p className="text-muted text-sm sm:text-base mt-4 font-light max-w-3xl">
            Enter your birth details once and switch between any of the 19 supported vargas — <b>D1 (Rasi), D2 (Hora), D3 (Drekkana), D4 (Chaturthamsa), D5 (Panchamamsa), D6 (Shashthamsa), D7 (Saptamsa), D8 (Ashtamsa), D9 (Navamsa), D10 (Dasamsa), D12 (Dwadasamsa), D16 (Shodasamsa), D20 (Vimsamsa), D24 (Chaturvimsamsa), D27 (Saptavimsamsa), D30 (Trimsamsa), D40 (Khavedamsa), D45 (Akshavedamsa), D60 (Shashtiamsa)</b>. Each chart shows sign, house, the planet's <b>divisional nakshatra + pada + degrees</b> inside that varga, natal nakshatra, deity (where applicable) and an insight that combines all of them.
          </p>
        </header>

        <form onSubmit={submit} className="rounded-2xl p-6 sm:p-7 bg-[#FAF4E8] border border-soft space-y-5 max-w-2xl" data-testid="divisional-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="space-y-1">
              <span className="text-xs text-muted">Date of birth</span>
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full px-3 py-2.5 rounded-md border border-soft bg-white" data-testid="divisional-date" required />
            </label>
            <label className="space-y-1">
              <span className="text-xs text-muted">Time of birth</span>
              <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="w-full px-3 py-2.5 rounded-md border border-soft bg-white" data-testid="divisional-time" required />
            </label>
          </div>
          <div className="relative">
            <label className="space-y-1 block">
              <span className="text-xs text-muted">Place of birth</span>
              <input
                type="text" value={placeQuery}
                onChange={async (e) => {
                  const q = e.target.value;
                  setPlaceQuery(q);
                  if (q.trim().length >= 2) {
                    try {
                      const { data: sug } = await api.get("/places/search", { params: { q: q.trim() } });
                      setSuggestions(sug || []);
                    } catch { /* ignore */ }
                  } else setSuggestions([]);
                }}
                placeholder="Start typing — Mumbai, Delhi, London..."
                className="w-full px-3 py-2.5 rounded-md border border-soft bg-white"
                data-testid="divisional-place"
              />
            </label>
            {suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 max-h-60 overflow-y-auto card-surface rounded-md border border-soft z-20 shadow-lg">
                {suggestions.map((s, i) => (
                  <button type="button" key={i}
                    onClick={() => {
                      const label = s.name || s.label;
                      setForm({ ...form, place: label, timezone: s.timezone || "Asia/Kolkata" });
                      setPlaceQuery(label); setSuggestions([]);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-amber-50 text-sm border-b border-soft last:border-0"
                    data-testid={`divisional-place-option-${i}`}
                  >{s.name || s.label}</button>
                ))}
              </div>
            )}
          </div>
          <button type="submit" disabled={loading} className="btn-primary rounded-full px-7 py-3 font-medium disabled:opacity-50" data-testid="divisional-submit">
            {loading ? "Computing 19 vargas…" : <>View my divisional charts <ArrowRight size={16} className="inline ml-1" /></>}
          </button>
        </form>

        {data && chart && (
          <div className="mt-12 space-y-8" data-testid="divisional-result">
            {/* === DIVISION PICKER === */}
            <section className="rounded-2xl p-6 bg-gradient-to-br from-[#FFF8E7] to-[#FFE7C2] border border-amber-200" data-testid="division-picker">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <div className="label-eyebrow text-[#7C2D12]">Step 2 · pick a divisional chart</div>
                  <p className="text-sm text-[#3F2D1B] mt-1 font-light">Switch between vargas to see how each planet's strength changes across life-domains.</p>
                </div>
                <select
                  value={selectedDiv}
                  onChange={(e) => setSelectedDiv(e.target.value)}
                  className="px-4 py-3 rounded-full border border-amber-300 bg-white text-sm font-medium text-[#3F2D1B] focus:outline-none focus:ring-2 focus:ring-amber-400 min-w-[280px]"
                  data-testid="division-select"
                >
                  {(data.supported_divisions || []).map((d) => {
                    const c = data.charts?.[String(d)];
                    return (
                      <option key={d} value={String(d)} data-testid={`division-option-${d}`}>
                        {c?.label} · {c?.name}
                      </option>
                    );
                  })}
                </select>
              </div>
            </section>

            {/* === CHART HEADER === */}
            <section className="rounded-2xl p-6 bg-[#FAF4E8] border border-soft" data-testid="chart-header">
              <div className="label-eyebrow text-[#7C2D12]">{chart.label} · {chart.name}</div>
              <h2 className="font-serif-display text-3xl sm:text-4xl mt-2 font-light text-[#3F2D1B]">
                Lagna in <span className="text-gold">{chart.lagna.rashi}</span> <span className="text-muted text-base font-light">(Lord: {chart.lagna.rashi_lord})</span>
              </h2>
              <p className="text-sm text-muted mt-3 font-light leading-snug max-w-3xl">{chart.purpose}</p>
            </section>

            {/* === CHART + LEGEND === */}
            <PaywallGate
              tool="divisional"
              toolLabel="Divisional Charts (D1–D60)"
              birthDetails={{ date: form.date, time: form.time, place: form.place || placeQuery, timezone: form.timezone }}
              report={data}
            >
              <div className="space-y-8">
              <section className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="rounded-2xl p-5 bg-gradient-to-br from-[#FFF8E7] to-[#FFE7C2] border border-amber-200" data-testid="chart-visual">
                <div className="label-eyebrow text-[#7C2D12]">{chart.label} · {chart.name} chart</div>
                <div className="mt-4 flex justify-center">
                  {chartShape && <KundaliChart kundali={chartShape} size={320} />}
                </div>
                <p className="text-xs text-muted mt-3 text-center font-light">
                  {chart.label} divisional chart — {chartStyle === "south" ? "South-Indian" : "North-Indian"} style.
                </p>
              </div>
              <div className="rounded-2xl p-5 bg-white border border-soft">
                <div className="label-eyebrow text-muted">House themes in {chart.label}</div>
                <ul className="mt-3 grid grid-cols-1 gap-2 text-sm">
                  {Object.entries(chart.house_themes).map(([h, theme]) => (
                    <li key={h} className="flex gap-3">
                      <span className="font-medium text-[#7C2D12] w-12 flex-shrink-0">H{h}</span>
                      <span className="text-[#3F2D1B] font-light leading-snug">{theme}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            {/* === PLANET INSIGHTS === */}
            <section className="space-y-4" data-testid="planet-insights">
              <div className="label-eyebrow text-[#7C2D12] flex items-center gap-2">
                <Sparkle size={14} weight="duotone" /> Planet-by-planet insights in {chart.label}
              </div>
              {chart.insights.map((ins) => {
                const dt = DIGNITY_TINT[ins.dignity] || DIGNITY_TINT.neutral;
                const Icon = dt.icon;
                const planetMeta = chart.planets[ins.planet];
                return (
                  <article key={ins.planet} className="rounded-2xl overflow-hidden border border-soft" data-testid={`insight-${ins.planet.toLowerCase()}`}>
                    <div className="p-5 bg-white">
                      <div className="flex items-baseline gap-3 flex-wrap">
                        <Icon size={16} weight="duotone" className="text-[#7C2D12]" />
                        <h3 className="font-serif-display text-2xl font-light text-[#3F2D1B]">{ins.planet}</h3>
                        <span className={`text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full ${dt.bg} ${dt.text}`}>{dt.label}</span>
                        {planetMeta.retrograde && (
                          <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full bg-orange-100 text-orange-800">RETROGRADE</span>
                        )}
                      </div>
                      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                        <div>
                          <div className="text-muted">Sign in {chart.label}</div>
                          <div className="font-medium text-[#3F2D1B] mt-0.5">{ins.rashi} <span className="text-muted">(L: {planetMeta.rashi_lord})</span></div>
                          <div className="text-[10px] text-muted">{planetMeta.divisional_degrees_in_rashi}°</div>
                        </div>
                        <div>
                          <div className="text-muted">House from lagna</div>
                          <div className="font-medium text-[#3F2D1B] mt-0.5">H{ins.house}</div>
                        </div>
                        <div>
                          <div className="text-muted">Nakshatra in {chart.label}</div>
                          <div className="font-medium text-[#3F2D1B] mt-0.5" data-testid={`insight-${ins.planet.toLowerCase()}-div-nak`}>
                            {planetMeta.divisional_nakshatra} <span className="text-muted">· pada {planetMeta.divisional_pada}</span>
                          </div>
                          <div className="text-[10px] text-muted">Lord: {planetMeta.divisional_nakshatra_lord} · Deity: {planetMeta.divisional_nakshatra_deity}</div>
                        </div>
                        <div>
                          <div className="text-muted">Natal position</div>
                          <div className="font-medium text-[#3F2D1B] mt-0.5">{planetMeta.natal_rashi} <span className="text-muted">{planetMeta.natal_degrees_in_rashi}°</span></div>
                          <div className="text-[10px] text-muted">{planetMeta.natal_nakshatra} · {planetMeta.natal_nakshatra_deity}</div>
                        </div>
                      </div>
                      <p className="text-sm text-[#3F2D1B] mt-4 font-light leading-snug">{ins.text}</p>
                    </div>
                    {ins.deity && (
                      <div className="px-5 py-4 bg-gradient-to-br from-[#F5EDFE] to-[#E9DDFB] border-t border-violet-200">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full bg-violet-200 text-violet-950">DEITY</span>
                          <span className="font-serif-display text-lg font-light text-[#3F2D1B]">{ins.deity.name}</span>
                        </div>
                        <p className="text-xs text-[#3F2D1B] mt-2 leading-snug font-light">{ins.deity.meaning}</p>
                      </div>
                    )}
                  </article>
                );
              })}
            </section>
              </div>
            </PaywallGate>

            <ToolAstrologerCta
              requiredTechnique="Vedic"
              pitch={`Get a deep walk-through of your ${chart.label} (${chart.name}) chart and how it interacts with your other vargas — with a Vedic specialist`}
              ctaLabel="Talk to"
            />
          </div>
        )}
      </div>
    </PageShell>
  );
}
